import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', profile: 'user', status: 'active' });
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/users');
      setUsuarios(res.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editingId}`, updateData);
        toast.success('Usuário atualizado!');
      } else {
        await api.post('/auth/register', formData);
        toast.success('Usuário cadastrado!');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, email: item.email, password: '', profile: item.profile, status: item.status });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir?')) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success('Usuário excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', profile: 'user', status: 'active' });
    setEditingId(null);
    unsavedChanges.resetChanges();
  };

  // Função para lidar com mudanças no formulário
  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    unsavedChanges.markAsChanged();
  };

  // Função para tentar fechar o dialog
  const handleDialogClose = (open) => {
    if (!open && unsavedChanges.hasChanges) {
      unsavedChanges.tryAction(() => {
        setDialogOpen(false);
        resetForm();
      });
    } else {
      setDialogOpen(open);
      if (!open) resetForm();
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;

  return (
    <div className="space-y-6" data-testid="usuarios-page">
      {/* Diálogo de confirmação para sair sem salvar */}
      <UnsavedChangesDialog
        isOpen={unsavedChanges.showDialog}
        onOpenChange={unsavedChanges.setShowDialog}
        onConfirmLeave={unsavedChanges.confirmLeave}
      />
      
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Usuários</h2>
          <p className="text-slate-600">Gerenciamento de usuários do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-usuario-button">
              <Plus className="mr-2" size={20} />Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editingId ? 'Editar' : 'Novo'} Usuário</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="usuario-form">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={formData.name} onChange={(e) => handleFormChange('name', e.target.value)} required data-testid="usuario-name-input" />
              </div>
              <div className="space-y-2">
                <Label>E-mail *</Label>
                <Input type="email" value={formData.email} onChange={(e) => handleFormChange('email', e.target.value)} required data-testid="usuario-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Senha {editingId ? '(deixe em branco para manter)' : '*'}</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required={!editingId} data-testid="usuario-password-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Perfil *</Label>
                  <select value={formData.profile} onChange={(e) => setFormData({ ...formData, profile: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="usuario-profile-select">
                    <option value="user">Usuario (Apenas visualizacao)</option>
                    <option value="supervisor">Supervisor (Cadastros)</option>
                    <option value="admin">Administrador (Acesso total)</option>
                    <option value="secretaria">Secretaria (Acesso por senha)</option>
                  </select>
                  <p className="text-xs text-slate-500">
                    {formData.profile === 'user' && '• Apenas visualizacao, sem alteracoes'}
                    {formData.profile === 'supervisor' && '• Pode fazer cadastros, mas nao lancamentos'}
                    {formData.profile === 'admin' && '• Acesso total ao sistema'}
                    {formData.profile === 'secretaria' && '• Acesso restrito a sua secretaria, mediante senha'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Status *</Label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="usuario-status-select">
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="usuario-submit-button">Salvar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Lista de Usuários</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usuarios.map((item) => (
                <TableRow key={item.id} className="table-row" data-testid={`usuario-row-${item.id}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.profile === 'admin' ? 'bg-blue-100 text-blue-800' : 
                      item.profile === 'supervisor' ? 'bg-purple-100 text-purple-800' : 
                      item.profile === 'secretaria' ? 'bg-pink-100 text-pink-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {item.profile === 'admin' ? 'Administrador' : 
                       item.profile === 'supervisor' ? 'Supervisor' : 
                       item.profile === 'secretaria' ? 'Secretaria' : 'Usuario'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${item.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {item.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(item)} data-testid={`edit-usuario-${item.id}`}><Pencil size={16} /></Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(item.id)} data-testid={`delete-usuario-${item.id}`}><Trash2 size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {usuarios.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Nenhum usuário cadastrado</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}